import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Formik } from "formik";
import { useTranslation } from "react-i18next";
import { DefaultLayout } from "../../../../layouts/Default";
import { TopNavigation } from "../../../../themes/Default/components/TopNavigation";
import { Button, ButtonBack } from "../../../../themes/Default/components/TopNavigation/components/Button";
import { Tabs } from "../../../../themes/Default/components/Tabs";
import { Main } from "../../../../themes/Default/components/Main";
import { Breadcrumps } from "../../../../themes/Default/components/Breadcrumps";
import { Link } from "../../../../themes/Default/components/Breadcrumps/components/Link";
import { Form } from "../../../../themes/Default/components/Form";
import { Row } from "../../../../themes/Default/components/Form/components/Row";
import { Field } from "../../../../themes/Default/components/Form/components/Row/components/Field";
import { List } from "../../../../themes/Default/components/List";
import { Header as ListHeader } from "../../../../themes/Default/components/List/components/Header";
import { Title as ListTitle } from "../../../../themes/Default/components/List/components/Header/components/Title";
import { Body as ListBody } from "../../../../themes/Default/components/List/components/Body";
import { TableHead } from "../../../../themes/Default/components/List/components/Body/components/TableHead";
import { TableRow } from "../../../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow";
import { TableHeadCell } from "../../../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow/components/TableHeadCell";
import { TableBody } from "../../../../themes/Default/components/List/components/Body/components/TableBody";
import { TableDataCell } from "../../../../themes/Default/components/List/components/Body/components/TableBody/components/TableDataCell";
import { Slide, toast, ToastContainer } from "react-toastify";
import { useUserToken } from "../../../../hooks/core/UserToken";
import { Textarea } from "../../../../themes/Default/components/Form/components/Textarea";
import { employeeWeeklyReportManagement } from "../../../../hooks/api/EmployeeWeeklyReportManagement/EmployeeWeeklyReportManagement";
import { Title } from "../../../../themes/Default/components/Title";

const WeeklyReportNotes = () => {
    const { t } = useTranslation("common");
    const formRef = useRef(null);
    const navigate = useNavigate();
    const { employeeWeeklyReport } = useParams();
    const [formErrors, setFormErrors] = useState([]);
    const { getDecoded } = useUserToken()[0];
    const decodedToken = getDecoded();
    const [report, setReport] = useState(null);

    const {
        useAddEmployeeWeeklyReportNoteMutation,
        useEmployeeWeeklyReportNoteListMutation,
        useViewEmployeeWeeklyReportMutation
    } = employeeWeeklyReportManagement;

    const [notesList] = useEmployeeWeeklyReportNoteListMutation();
    const [addNote] = useAddEmployeeWeeklyReportNoteMutation();
    const [viewReport] = useViewEmployeeWeeklyReportMutation();

    const [notes, setNotes] = useState();

    const tabs = [{ title: t("details"), link: "/settings/reports", isActive: true }];

    const onSave = () => {
        if (formRef.current) {
            formRef.current.handleSubmit();
        }
    };

    const gotoEdit = (weeklyReportNoteId) => {
        navigate("/reports/view/" + employeeWeeklyReport + "/notes/" + weeklyReportNoteId);
    };

    const onSaved = (values) => {
        const body = {...values};
        addNote({body, weeklyReportId: employeeWeeklyReport}).then((result) => {
            if (result?.error) {
                setFormErrors(result?.error?.data?.error?.violations);
                toast.error(result.error.data.error.responseMessage);
            } else {
                toast.info(result.data.info.responseMessage);
                loadNotes();
            }
        });
    };

    const loadNotes = () => {
        notesList({weeklyReportId: employeeWeeklyReport}).then((result) => {
            setNotes(result?.data?.records);
        });
    };

    useEffect(() => {
        loadNotes();
        viewReport(employeeWeeklyReport).then((result) => {
            setReport(result?.data);
        });
    }, []);

    return (
        <DefaultLayout>
            
            <TopNavigation>
                <ButtonBack to={"/reports/view/" + employeeWeeklyReport} />
                <Button title={t('save')} icon="fa-regular fa-floppy-disk" onClick={onSave}></Button>
            </TopNavigation>
            <Tabs tabs={tabs} />
            <Main>
                <Title>{report?.employeeName + " (" + report?.startDateFormated + " - " + report?.endDate + ")"}</Title>
                <Breadcrumps>
                    <Link title={t("report.title")} icon="fa-light fa-clipboard" />
                    <Link title={t("report.view")} isActive={true} />
                </Breadcrumps>
                <Formik
                    innerRef={formRef}
                    initialValues={{ 
                        note: "", 
                    }}
                    onSubmit={(values, { resetForm }) => {
                        onSaved(values);
                        resetForm();
                    }}
                >
                    {({ values, handleChange, setFieldValue, resetForm }) => (
                        <Form>
                            <Row>
                                <Field isFullWidth={true}>
                                    <Textarea 
                                        name="note" 
                                        label={t('groups.progressNote')} 
                                        rows={5}
                                        value={values.note} 
                                        onChange={handleChange} 
                                        required={true} 
                                        error={formErrors?.note}
                                    />
                                </Field>
                            </Row>
                        </Form>
                    )}
                </Formik>
                <br />
                <List>
                    <ListHeader>
                        <ListTitle 
                        />
                    </ListHeader>
                    <ListBody>
                        <TableHead>
                            <TableRow>
                                <TableHeadCell></TableHeadCell>
                                <TableHeadCell><span>{t('groups.progressNote')}</span></TableHeadCell>
                                <TableHeadCell><span>{t('Datum')}</span></TableHeadCell>
                                <TableHeadCell><span>{t('Vreme')}</span></TableHeadCell>
                                <TableHeadCell><span>{t('Autor')}</span></TableHeadCell>
                                <TableHeadCell></TableHeadCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                        {notes?.length > 0 &&
                            notes?.map((note, index) => {
                                return (
                                    <TableRow key={index}>
                                        <TableDataCell>
                                            {decodedToken?.id === note.createdBy ? (
                                                <Button icon="fa-regular fa-pen" onClick={() => gotoEdit(note.id)}/>
                                            ) : null}
                                        </TableDataCell>
                                        <TableDataCell>{note.note}</TableDataCell>
                                        <TableDataCell>{note.date}</TableDataCell>
                                        <TableDataCell>{note.time}</TableDataCell>
                                        <TableDataCell>{note.createdByFullName}</TableDataCell>
                                        <TableDataCell></TableDataCell>
                                    </TableRow>
                                )
                        })}
                        </TableBody>
                    </ListBody>
                </List>
            </Main>
        </DefaultLayout>
    );
};

export default WeeklyReportNotes;